import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { useOpenTickets } from '../context/OpenTicketsContext'
import { waitingQty } from '../utils/serving'
import type { Order, OrderLine } from '../types'

export interface WaitingLine {
  line: OrderLine
  waiting: number
  since: number
}

export interface WaitingTable {
  ticket: Order
  lines: WaitingLine[]
  /** The oldest wait at this table, for ordering and the header. */
  oldest: number
}

/**
 * The signed-in person's own units still to take to tables, table by table,
 * oldest first. One source for the Sipariş Takip page, the shortcut's count
 * and the 10/20-minute reminders, so the three never disagree.
 *
 * Re-renders every 30 s so "12 dk" keeps moving without anyone touching it.
 */
export function useMyWaiting(): { tables: WaitingTable[]; now: number; enabled: boolean } {
  const { admin, canTrack } = useAuth()
  const { openTickets } = useOpenTickets()
  const enabled = canTrack
  const me = admin?.displayName || admin?.username || ''

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!enabled) return
    const t = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(t)
  }, [enabled])

  const tables = useMemo(() => {
    if (!enabled || !me) return []
    const out: WaitingTable[] = []
    for (const ticket of openTickets) {
      const lines: WaitingLine[] = ticket.lines
        .filter((l) => (l.byName ?? '') === me && waitingQty(l) > 0)
        .map((l) => ({ line: l, waiting: waitingQty(l), since: l.waitingSince ?? now }))
        .sort((a, b) => a.since - b.since)
      if (lines.length > 0) out.push({ ticket, lines, oldest: lines[0].since })
    }
    return out.sort((a, b) => a.oldest - b.oldest)
  }, [enabled, me, openTickets, now])

  return { tables, now, enabled }
}
